import { ConnectionProtocolEnum } from './connection-protocol';
import { ExpressionDTO } from '@/entities/user/expression/@x/user';
import { z } from 'zod/v4';

/** Base attribute schema */
export const LDAPBaseAttributeSchemaDTO = z.object({
  login_expr: ExpressionDTO,
  name_expr: ExpressionDTO,
  email_expr: ExpressionDTO,
  surname_expr: ExpressionDTO,
  patronymic_expr: ExpressionDTO,
  status_expr: ExpressionDTO,
  avatar_expr: ExpressionDTO,
});
export type LDAPBaseAttributeSchema = z.infer<
  typeof LDAPBaseAttributeSchemaDTO
>;

// /** Connection schema (id, login, password) */
// export const LDAPConnectionDTO = z.object({
//   id: z.uuid(),
//   login: z.string().optional(),
//   password: z.string().optional(),
// });
// export type LDAPConnection = z.infer<typeof LDAPConnectionDTO>;

/** Full connection DTO */
export const LDAPConnectionDTO = z.object({
  id: z.uuid(),
  host: z.string(),
  port: z.number(),
  protocol: z.nativeEnum(ConnectionProtocolEnum).optional(),
  ignore_cert: z.boolean().optional(),
  base_dn: z.string(),
  group_dn: z.array(z.string()).optional(),
  user_dn: z.array(z.string()).optional(),
  group_template: z.string(),
  user_template: z.string(),
  anonymous: z.boolean().optional(),
  login: z.string().optional(),
  password: z.string().optional(),
  name: z.string(),
  is_active: z.boolean(),
});
export type LDAPConnection = z.infer<typeof LDAPConnectionDTO>;
/** Full connection DTO */
export const LDAPConnectionResultDTO = z.object({
  is_connected: z.boolean(),
  detail: z.string().optional(),
});
export type LDAPConnectionResult = z.infer<typeof LDAPConnectionResultDTO>;
/** Create = omit id from full DTO */
export const LDAPConnectionCreateDTO = LDAPConnectionDTO.omit({ id: true });
export type LDAPConnectionCreate = z.infer<typeof LDAPConnectionCreateDTO>;

/** Extended attribute */
export const LDAPExtendedAttributeSchemaDTO = z.object({
  user_attribute_id: z.uuid().optional(),
  expression: ExpressionDTO,
  value_type: z.string().optional(),
  name: z.string().optional(),
});
export type LDAPExtendedAttributeSchema = z.infer<
  typeof LDAPExtendedAttributeSchemaDTO
>;

/** Extended response */
export const LDAPExtendedDTO = z.object({
  host: z.string(),
  port: z.number(),
  protocol: z.nativeEnum(ConnectionProtocolEnum).optional(),
  ignore_cert: z.boolean().optional(),
  base_dn: z.string(),
  group_dn: z.array(z.string()).optional(),
  user_dn: z.array(z.string()).optional(),
  anonymous: z.boolean().optional(),
  login: z.string().optional(),
  password: z.string().optional(),
  name: z.string(),
  is_active: z.boolean(),
  id: z.uuid(),
  base_attr: LDAPBaseAttributeSchemaDTO,
  extended_attr: z.array(LDAPExtendedAttributeSchemaDTO).optional(),
});
export type LDAPExtended = z.infer<typeof LDAPExtendedDTO>;

/** Short LDAP entry */
export const LDAPDTO = z.object({
  host: z.string(),
  port: z.number(),
  protocol: z.nativeEnum(ConnectionProtocolEnum).optional(),
  ignore_cert: z.boolean().optional(),
  base_dn: z.string(),
  group_dn: z.array(z.string()).optional(),
  user_dn: z.array(z.string()).optional(),
  anonymous: z.boolean().optional(),
  login: z.string().optional(),
  password: z.boolean(),
  name: z.string(),
  is_active: z.boolean(),
  id: z.uuid(),
});
export type LDAP = z.infer<typeof LDAPDTO>;

/** Server schema */
export const LDAPServerDTO = z.object({
  id: z.uuid(),
  display_name: z.string(),
  domain: z.string(),
});
export type LDAPServer = z.infer<typeof LDAPServerDTO>;

/** Test connection schema */
export const LDAPTestConnectionDTO = z.object({
  host: z.string(),
  port: z.number(),
  protocol: z.nativeEnum(ConnectionProtocolEnum).optional(),
  ignore_cert: z.boolean().optional(),
  base_dn: z.string(),
  group_dn: z.array(z.string()).optional(),
  user_dn: z.array(z.string()).optional(),
  anonymous: z.boolean().optional(),
  login: z.string().optional(),
  password: z.string().optional(),
  id: z.uuid().optional(),
});
export type LDAPTestConnection = z.infer<typeof LDAPTestConnectionDTO>;

/**
 * Auth v3 LDAP settings (new API, used in Settings → Users → LDAP).
 */
export const LDAPAttributeSyncTypeDTO = z.enum([
  'DISABLE',
  'PERIODIC',
  'ON_CREATE',
  'IF_EXIST',
]);
export type LDAPAttributeSyncType = z.infer<typeof LDAPAttributeSyncTypeDTO>;

export const LDAPAttributeExpressionDTO = z.object({
  name: z.string(),
  expression: z.string(),
  sync_type: LDAPAttributeSyncTypeDTO,
});
export type LDAPAttributeExpression = z.infer<
  typeof LDAPAttributeExpressionDTO
>;

export const LDAPCreateDTO = z.object({
  name: z.string(),
  display_name: z.string(),
  active: z.boolean(),

  host: z.string(),
  port: z.number().int().min(0).max(65535),
  protocol: z.string().nullable(),
  ignore_cert: z.boolean(),

  anonymous: z.boolean(),
  login: z.string().nullable(),
  password: z.string().nullable(),

  base_dn: z.string(),
  user_dn: z.array(z.string()),
  group_dn: z.array(z.string()),
  user_template: z.string(),
  group_template: z.string(),

  login_expression: z.string(),
  attribute_expressions: z.array(LDAPAttributeExpressionDTO),
});
export type LDAPCreate = z.infer<typeof LDAPCreateDTO>;

export const LDAPUpdateDTO = LDAPCreateDTO;
export type LDAPUpdate = z.infer<typeof LDAPUpdateDTO>;

export const LDAPResponseDTO = LDAPCreateDTO.and(
  z.object({
    ldap_id: z.string().uuid().optional(),
    id: z.string().uuid().optional(),
  }),
);
export type LDAPResponse = z.infer<typeof LDAPResponseDTO>;

export function getLdapId(data: Partial<LDAPResponse> | null | undefined) {
  return (data?.ldap_id || data?.id) ?? null;
}

export const LDAPTemplateDTO = z.object({
  group_template: z.string(),
  user_template: z.string(),
});
export type LDAPTemplate = z.infer<typeof LDAPTemplateDTO>;

export const LDAPUserInfoTestRequestDTO = z.object({
  ldap_id: z.string().uuid(),
  login: z.string(),
  password: z.string(),
});
export type LDAPUserInfoTestRequest = z.infer<
  typeof LDAPUserInfoTestRequestDTO
>;

export const LDAPUserInfoTestResponseDTO = z.object({
  user: z.unknown().optional(),
  user_info: z.unknown().optional(),
  success: z.boolean(),
  provider: z.string().optional(),
  error_message: z.string().optional(),
  error_vars: z.record(z.string(), z.unknown()).optional(),
});
export type LDAPUserInfoTestResponse = z.infer<
  typeof LDAPUserInfoTestResponseDTO
>;